import axios from 'axios';

const api = axios.create({ baseURL: '/api', headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('projets-token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Session expirée ou habilitation retirée : retour à l'écran de connexion.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !error.config.url.startsWith('/auth/')) {
      localStorage.removeItem('projets-token');
      window.location.assign('/');
    }
    return Promise.reject(error);
  }
);

// Message lisible pour l'utilisateur, quelle que soit l'erreur.
export const messageErreur = (e) =>
  e?.response?.data?.error
  // Refus du proxy nginx avant le serveur (corps trop gros) : pas de JSON.
  || (e?.response?.status === 413 ? 'Fichier trop volumineux (10 Mo au plus).' : null)
  || (e?.response ? `Erreur ${e.response.status}` : 'Serveur injoignable — vérifiez votre connexion.');

const donnees = (p) => p.then((r) => r.data);

export const auth = {
  getConfig: () => api.get('/auth/config'),
  microsoftStart: () => api.get('/auth/microsoft/start'),
  microsoftCallback: (code, state) => api.post('/auth/microsoft/callback', { code, state }),
  getMe: () => api.get('/auth/me'),
  preferences: (p) => donnees(api.put('/auth/me/preferences', p)),
};

export const referentiels = {
  utilisateurs: () => donnees(api.get('/utilisateurs')).then((d) => d.utilisateurs),
  entreprises: () => donnees(api.get('/entreprises')).then((d) => d.entreprises),
};

export const projets = {
  liste: (params) => donnees(api.get('/projets', { params })).then((d) => d.projets),
  fresque: (params) => donnees(api.get('/projets/fresque', { params })),
  detail: (id) => donnees(api.get(`/projets/${id}`)),
  creer: (p) => donnees(api.post('/projets', p)),
  modifier: (id, p) => donnees(api.put(`/projets/${id}`, p)),
  deplacer: (id, statut, position) => donnees(api.patch(`/projets/${id}/deplacer`, { statut, position })),
  membres: (id, membres) => donnees(api.put(`/projets/${id}/membres`, { membres })),
  supprimer: (id) => donnees(api.delete(`/projets/${id}`)),
};

export const taches = {
  miennes: (params) => donnees(api.get('/taches', { params })).then((d) => d.taches),
  detail: (id) => donnees(api.get(`/taches/${id}`)).then((d) => d.tache),
  creer: (t) => donnees(api.post('/taches', t)),
  modifier: (id, t) => donnees(api.put(`/taches/${id}`, t)).then((d) => d.tache),
  deplacer: (id, statut, position) => donnees(api.patch(`/taches/${id}/deplacer`, { statut, position })),
  dates: (id, dateDebut, dateEcheance) => donnees(api.patch(`/taches/${id}/dates`, { date_debut: dateDebut, date_echeance: dateEcheance })).then((d) => d.tache),
  supprimer: (id) => donnees(api.delete(`/taches/${id}`)),
  messages: (id) => donnees(api.get(`/taches/${id}/messages`)).then((d) => d.messages),
  ecrire: (id, texte, mentions) => donnees(api.post(`/taches/${id}/messages`, { texte, mentions })),
  effacerMessage: (id, messageId) => donnees(api.delete(`/taches/${id}/messages/${messageId}`)),
  fichiers: (id) => donnees(api.get(`/taches/${id}/fichiers`)).then((d) => d.fichiers),
  joindre: (id, fichier, onProgression) => donnees(api.post(`/taches/${id}/fichiers`, fichier, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Nom-Fichier': encodeURIComponent(fichier.name),
      'X-Type-Fichier': fichier.type || 'application/octet-stream',
    },
    onUploadProgress: (e) => e.total && onProgression?.(Math.round((100 * e.loaded) / e.total)),
  })),
  // Contenu en Blob : le jeton part dans l'en-tête, jamais dans une adresse.
  contenuFichier: (id, f) => api.get(`/taches/${id}/fichiers/${f.id}`, { responseType: 'blob' })
    .then((r) => new Blob([r.data], { type: f.type_mime })),
  retirerFichier: (id, fichierId) => donnees(api.delete(`/taches/${id}/fichiers/${fichierId}`)),
};

export const notifications = {
  liste: () => donnees(api.get('/notifications')),
  lue: (id) => donnees(api.post(`/notifications/${id}/lue`)),
  toutLu: () => donnees(api.post('/notifications/tout-lu')),
};

export const tableauDeBord = (perimetre) => donnees(api.get('/tableau-de-bord', { params: { perimetre } }));

export const admin = {
  etat: () => donnees(api.get('/admin/etat')),
  synchroCrm: () => donnees(api.post('/admin/synchro-crm')),
  habilitations: () => donnees(api.get('/admin/habilitations')).then((d) => d.habilitations),
  creerHabilitation: (h) => donnees(api.post('/admin/habilitations', h)),
  modifierHabilitation: (id, h) => donnees(api.put(`/admin/habilitations/${id}`, h)),
  entreprises: (type) => donnees(api.get('/admin/entreprises', { params: { type } })).then((d) => d.entreprises),
  creerEntreprise: (e) => donnees(api.post('/admin/entreprises', e)),
  modifierEntreprise: (id, e) => donnees(api.put(`/admin/entreprises/${id}`, e)),
  supprimerEntreprise: (id) => donnees(api.delete(`/admin/entreprises/${id}`)),
};

export default api;
